import { alpha } from '@mui/material/styles';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import UploadIcon from 'assets/icons/upload.svg';
import Loading from 'components/common/Loading';
import { array, bool, func, node, object, oneOfType, string } from 'prop-types';
import React from 'react';
import Dropzone from 'react-dropzone';
import { I18n } from 'utils/i18n';
import toDropzoneAccept from 'utils/toDropzoneAccept';

/** Horizontal “Or” rule — muted lines + secondary label (same as Acknowledge create). */
export function DropzoneOrDivider() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        maxWidth: 400,
        my: 2,
        px: 0.5,
      }}
    >
      <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
      <Typography
        variant="body2"
        component="span"
        color="textSecondary"
        sx={{ px: 2, fontWeight: 400, fontSize: '0.875rem' }}
      >
        {I18n.t('AcknowledgementCreate.Or')}
      </Typography>
      <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
    </Box>
  );
}

export function DropzoneStandardPlaceholder({ hint = null, hintSecondary = null }) {
  const hintNode = hint ?? I18n.t('AcknowledgementCreate.Drag & drop file here');

  return (
    <>
      <SvgIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }}>
        <UploadIcon />
      </SvgIcon>
      <Box sx={{ textAlign: 'center', color: 'text.primary', px: 1 }}>
        {typeof hintNode === 'string' ? (
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            {hintNode}
          </Typography>
        ) : (
          hintNode
        )}
        {hintSecondary ? (
          <Box sx={{ mt: 0.75, color: 'text.secondary' }}>{hintSecondary}</Box>
        ) : null}
      </Box>
      <DropzoneOrDivider />
      <Button
        type="button"
        variant="outlined"
        color="secondary"
        size="small"
        sx={{ mt: 0.5, textTransform: 'none' }}
      >
        {I18n.t('AcknowledgementCreate.Browse file')}
      </Button>
    </>
  );
}

DropzoneStandardPlaceholder.propTypes = {
  hint: node,
  hintSecondary: node,
};

const getDropzoneSx = (hasImage, { isDragAccept, isDragReject }) => [
  {
    borderRadius: '4px',
    padding: hasImage ? 0 : '36px 28px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'text.primary',
    cursor: 'pointer',
    outlineColor: 'primary.main',
    width: '100%',
    height: '100%',
    border: hasImage ? '2px solid transparent' : theme => `2px dashed ${theme.palette.divider}`,
    backgroundColor: 'transparent',
  },
  isDragAccept && {
    background: theme => alpha(theme.palette.primary.main, 0.06),
    borderColor: theme => alpha(theme.palette.primary.main, 0.45),
    borderStyle: 'dashed',
  },
  isDragReject && {
    background: '#ffeeee',
    borderColor: theme => theme.palette.danger,
    borderStyle: 'solid',
  },
];

/**
 * Framed upload control: outer frame `primary.ultraLight`, inner target transparent + dashed divider border + icon / hint / Or / browse.
 * Override outer fill with `frameSx` (e.g. success tint). Pass `placeholder` only for full replacement (e.g. success card).
 * Pass `prependContent` to render inside the dashed border above the standard upload UI (Acknowledge-style template row).
 */
const DropzoneBox = ({
  sx = {},
  frameSx = {},
  onDrop,
  loading = false,
  error = false,
  accept = 'application/pdf',
  multiple = false,
  image = null,
  placeholder = null,
  prependContent = null,
  hint = null,
  hintSecondary = null,
  disableClick = false,
  framed = true,
}) => {
  const hasImage = !!image;
  const showFrame = framed && !image;

  const content = loading ? (
    <Loading size={80} />
  ) : (
    <Dropzone
      accept={toDropzoneAccept(accept)}
      onDrop={onDrop}
      noClick={disableClick}
      multiple={multiple}
    >
      {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }) => {
        const uploadPlaceholder =
          placeholder ||
          (image ? null : (
            <DropzoneStandardPlaceholder hint={hint} hintSecondary={hintSecondary} />
          ));

        const showMainBlock = !isDragActive && !image;
        const hasPrepend = Boolean(prependContent);

        return (
          <Box {...getRootProps()} sx={getDropzoneSx(hasImage, { isDragAccept, isDragReject })}>
            <input {...getInputProps()} />
            {isDragAccept && <p>{I18n.t('AcknowledgementCreate.Drop the file to upload')}</p>}
            {isDragReject && <p>{I18n.t('AcknowledgementCreate.File type not accepted')}</p>}
            {showMainBlock && (
              <>
                {hasPrepend ? (
                  <Box
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      alignSelf: 'stretch',
                    }}
                  >
                    {prependContent}
                  </Box>
                ) : null}
                {hasPrepend && uploadPlaceholder ? <DropzoneOrDivider /> : null}
                {uploadPlaceholder}
              </>
            )}
          </Box>
        );
      }}
    </Dropzone>
  );

  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          minHeight: 210,
          width: '100%',
          ...(image
            ? {
                backgroundImage: `url(${image})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }
            : {}),
        },
        showFrame && {
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'primary.ultraLight',
          boxSizing: 'border-box',
        },
        frameSx,
        sx,
      ]}
    >
      {content}
      {error && (
        <Typography color="error" align="center">
          {I18n.t('Something went wrong with sending data')}
        </Typography>
      )}
    </Box>
  );
};

DropzoneBox.propTypes = {
  onDrop: func.isRequired,
  loading: bool,
  error: bool,
  accept: oneOfType([string, object]),
  multiple: bool,
  image: string,
  sx: object,
  frameSx: oneOfType([object, array, func]),
  /** Full replacement of inner content (e.g. uploaded file summary). */
  placeholder: node,
  /** Rendered inside the dashed border above “Or” + upload (clicks do not open the file picker). */
  prependContent: node,
  /** Main line(s) between icon and Or (string or node). Defaults to AcknowledgementCreate.Drag & drop file here. */
  hint: node,
  hintSecondary: node,
  disableClick: bool,
  framed: bool,
};

export default DropzoneBox;
